import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { FC } from "react";

import { addCreditEvaluationIncome } from "api/creditEvaluations";
import { creditEvaluationsKeys } from "api/creditEvaluations/queries";

import AddIncomeForm, { IncomeFormValues } from "./IncomeForm";

interface AddIncomeModalProps {
  creditEvaluationId: string;
  isOpen: boolean;
  handleClose: () => void;
  initialValues?: any;
}

const AddIncomeModal: FC<AddIncomeModalProps> = ({
  creditEvaluationId,
  isOpen,
  handleClose,
  initialValues = undefined,
}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: addIncome, status } = useMutation(async (data: any) => {
    const res = await addCreditEvaluationIncome(creditEvaluationId, data);
    await queryClient.invalidateQueries(
      creditEvaluationsKeys.details(creditEvaluationId),
    );

    return res.data;
  });

  const handleSubmit = async (values: IncomeFormValues) => {
    const mappedValues: any = {
      ...values,
    };

    await addIncome(mappedValues, {
      onSuccess: () => handleClose(),
    });
  };

  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          py: 2,
          pb: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandSuccess?.secondary ?? "#E5F9EC",
              borderRadius: "50%",
            }}
          >
            <Plus
              size={32}
              color={(theme.palette as any).brandSuccess?.primary ?? "#2CB666"}
            />
          </Stack>
          <Typography variant="headingMD" fontWeight={600}>
            Add Income
          </Typography>
          <Typography
            variant="textSM"
            textAlign="center"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Add income source to this credit evaluation.
          </Typography>
        </Stack>
      </DialogTitle>
      <AddIncomeForm
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        submitStatus={status}
        initialValues={initialValues}
      />
    </Dialog>
  );
};

export default AddIncomeModal;
